import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Lang = "fr" | "en" | "es";
export const DATE_LOCALE: Record<Lang, string> = { fr: "fr-FR", en: "en-US", es: "es-ES" };
export const LANGS: { id: Lang; label: string; flag: string }[] = [
  { id: "fr", label: "Français", flag: "FR" },
  { id: "en", label: "English", flag: "EN" },
  { id: "es", label: "Español", flag: "ES" },
];

// French is the source language (the keys). EN/ES provide overrides; a missing entry falls
// back to the French key, so the UI is never blank.
const EN: Record<string, string> = {
  "90 % reversés sur tes ventes": "90% paid out on your sales",
  "Ajoute-en une dans la Ferme": "Add one in the Farm",
  "Ajuster le restant": "Adjust remaining",
  "Aperçu du projet": "Project overview",
  "Aucun fichier": "No files",
  "Aucun fichier. Génère un objet ou décris une idée ci-dessus.": "No files. Generate an object or describe an idea above.",
  "Aucune bobine. Ajoute ton inventaire de filament pour suivre conso et coûts.": "No spools. Add your filament inventory to track usage and costs.",
  "Aucune impression en cours. Routez un fichier prêt à imprimer depuis un projet.": "No prints running. Route a print-ready file from a project.",
  "Aucune imprimante enregistrée.": "No printer registered.",
  "Boutique store.soclose.co dédiée": "Dedicated store.soclose.co shop",
  "CAD paramétrique (LLM)": "Parametric CAD (LLM)",
  "CAD paramétrique + 19 générateurs": "Parametric CAD + 19 generators",
  "Ce projet partagé ne contient aucun fichier.": "This shared project contains no files.",
  "Configuré": "Configured",
  "Connecteurs MMF / Thingiverse": "MMF / Thingiverse connectors",
  "Cookies & confidentialité": "Cookies & privacy",
  "Critique de conception": "Design critique",
  "Depuis une idée · IA": "From an idea · AI",
  "Dernière mise à jour :": "Last updated:",
  "Détails": "Details",
  "Détails du job": "Job details",
  "En file": "Queued",
  "Envoyé": "Sent",
  "Erreur": "Error",
  "Essentiels": "Essential",
  "Export STL / 3MF / G-code": "STL / 3MF / G-code export",
  "G-code requis : tranche le modèle d'abord.": "G-code required: slice the model first.",
  "Gate INSPECTOR illimité": "Unlimited INSPECTOR gate",
  "Générateur": "Generator",
  "Générateur paramétrique": "Parametric generator",
  "Génération IA (Ollama local)": "AI generation (local Ollama)",
  "Génération IA cloud (TRELLIS / Hunyuan3D)": "Cloud AI generation (TRELLIS / Hunyuan3D)",
  "Impression": "Printing",
  "Imprimante · matière": "Printer · material",
  "Lancer sur la ferme": "Launch on the farm",
  "Lien de partage invalide ou expiré.": "Invalid or expired share link.",
  "Local · prêt": "Local · ready",
  "Machine · matière": "Machine · material",
  "Maillage génératif (fal/kai)": "Generative mesh (fal/kai)",
  "Matières": "Materials",
  "Matériaux": "Materials",
  "Mesure d'audience anonymisée pour améliorer le service.": "Anonymized analytics to improve the service.",
  "Multi-imprimantes + Spoolman": "Multi-printer + Spoolman",
  "Nom de l'imprimante": "Printer name",
  "Note · dev · amélioration": "Note · dev · improvement",
  "Nous utilisons des cookies pour sécuriser le site, comprendre son usage et améliorer nos services. Les cookies essentiels sont toujours actifs. Vos données sont traitées conformément à la": "We use cookies to keep this site secure, understand how it is used and improve our services. Essential cookies are always on. Your data is processed in accordance with the",
  "Nécessaires au fonctionnement (session, sécurité).": "Required for the site to work (session, security).",
  "Parcours libre de la galerie": "Free gallery browsing",
  "Pas encore d'impressions terminées.": "No finished prints yet.",
  "Personnalisation et mesure des campagnes.": "Personalization and campaign measurement.",
  "Personnaliser": "Customize",
  "Pièce précédente": "Previous part",
  "Pièce suivante": "Next part",
  "Pièces": "Parts",
  "Planifié": "Scheduled",
  "Politique cookies": "Cookie policy",
  "Politique de confidentialité": "Privacy Policy",
  "Pour toute question de protection des données ou question légale, contactez": "For any data protection or legal question, contact",
  "Projet partagé · lecture seule": "Shared project · read-only",
  "Préférences cookies": "Cookie settings",
  "Prêtes": "Ready",
  "Quantité (copies)": "Quantity (copies)",
  "Rapport de printabilité": "Printability report",
  "Rechercher projets, fichiers, générateurs, pages…": "Search projects, files, generators, pages…",
  "Refuser le non-essentiel": "Reject non-essential",
  "Retour": "Back",
  "Retour à l'accueil": "Back to home",
  "Routage ferme d'impression": "Print-farm routing",
  "SLA & facturation entreprise": "SLA & enterprise billing",
  "Sans groupe": "No group",
  "Singapour": "Singapore",
  "Slice / estimer (temps · filament · coût)": "Slice / estimate (time · filament · cost)",
  "Slicing PrusaSlicer + devis": "PrusaSlicer slicing + quotes",
  "Support prioritaire": "Priority support",
  "Template de configuration": "Configuration template",
  "Terminé": "Done",
  "Tout Maker, plus :": "Everything in Maker, plus:",
  "Tout Studio, plus :": "Everything in Studio, plus:",
  "Tout accepter": "Accept all",
  "Vente sur 12 marketplaces": "Sell on 12 marketplaces",
  "au titre du": "under the",
  "batch · planifié": "batch · scheduled",
  "de": "of",
  "prêts à imprimer": "print-ready",
  "rendu global · toutes les pièces": "global render · all parts",
  "timing · notes · améliorations": "timing · notes · improvements",
  "À configurer": "To configure",
  "À lancer": "Ready to start",
  "Éditer": "Edit",
  "Étapes": "Steps",
  "État": "State",
  "éclatement": "exploded view",
  "/ vente sur la boutique SoClose.": "/ sale on the SoClose store.",
  "1 clic → prompt": "1 click → prompt",
  "API hors-ligne": "API offline",
  "Apparence, clé API, langue, compte.": "Appearance, API key, language, account.",
  "Aucun job. Routez un modèle validé vers une imprimante.": "No jobs. Route a validated model to a printer.",
  "Aucun projet ne correspond. Crée un projet ou ajuste les filtres.": "No project matches. Create a project or adjust the filters.",
  "Aucun projet.": "No projects yet.",
  "Bibliothèque de templates": "Template library",
  "Bras articulé, boîtier capteur…": "Articulated arm, sensor housing…",
  "Clé effacée": "Key cleared",
  "Clé requise pour voir l'état": "Key required to see the status",
  "Commerciale (vente)": "Commercial (sale)",
  "Conception…": "Designing…",
  "Confort": "Comfy",
  "Connexion": "Sign in",
  "Continuer avec GitHub": "Continue with GitHub",
  "Continuer avec Google": "Continue with Google",
  "Créateur": "Creator",
  "Créer le projet": "Create the project",
  "Créer le projet + concevoir": "Create the project + design",
  "Créer mon compte": "Create my account",
  "Créez-en un": "Create one",
  "Dernières générations": "Latest generations",
  "Description": "Description",
  "Destinations": "Destinations",
  "Dupliqué": "Duplicated",
  "Déconnecté": "Signed out",
  "Décris ton idée": "Describe your idea",
  "En créant un compte, tu acceptes les conditions et la politique de confidentialité.": "By creating an account, you accept the terms and the privacy policy.",
  "En vente": "For sale",
  "Fait à Singapour": "Made in Singapore",
  "Filtrer les projets…": "Filter projects…",
  "Glisse ton fichier ou clique pour choisir": "Drop your file or click to choose",
  "Groupe / collection": "Group / collection",
  "Génération…": "Generating…",
  "Générer + valider": "Generate + validate",
  "Géométrie exacte via le CAD kernel, puis gate INSPECTOR avant ajout au projet.": "Exact geometry via the CAD kernel, then the INSPECTOR gate before adding to the project.",
  "Impression en cours": "Printing now",
  "Inscription": "Sign up",
  "Inscris-toi": "Sign up",
  "L'atelier 3D de bout en bout : idée → maillage validé → imprimé ou vendu.": "The end-to-end 3D workshop: idea → validated mesh → printed or sold.",
  "Les lectures publiques (galerie, previews) ne nécessitent rien. Les actions (créer, router, publier) et les données opérationnelles exigent ta clé": "Public reads (gallery, previews) need nothing. Actions (create, route, publish) and operational data require your key",
  "Licence": "License",
  "Lien de partage copié": "Share link copied",
  "Légal": "Legal",
  "Machine": "Machine",
  "Matière": "Material",
  "Mettre en vente": "Put on sale",
  "Modifier (nom, groupe)": "Edit (name, group)",
  "Modifier le projet": "Edit the project",
  "Mon support de caméra": "My camera mount",
  "Monétisation": "Monetization",
  "Mot de passe": "Password",
  "Nom du projet (optionnel)": "Project name (optional)",
  "Nouveau projet": "New project",
  "Optionnel — contexte, contraintes, machine cible…": "Optional — context, constraints, target machine…",
  "Ouvrir": "Open",
  "Ouvrir dans le Studio 3D": "Open in the 3D Studio",
  "Paramètres": "Parameters",
  "Parcours librement la galerie. Pour télécharger, remixer ou générer, crée ton compte. Téléverse et": "Browse the gallery freely. To download, remix or generate, create your account. Upload and",
  "Partage libre pour un max de portée et de téléchargements.": "Free sharing for maximum reach and downloads.",
  "Pas encore de compte ?": "No account yet?",
  "Prix (€)": "Price (€)",
  "Profil enregistré": "Profile saved",
  "Projet créé": "Project created",
  "Projet mis à jour": "Project updated",
  "Projets récents": "Recent projects",
  "Précédent": "Previous",
  "Publie gratuitement pour la portée, ou fixe ton prix et vends sur la boutique SoClose et les marketplaces. Tu gardes": "Publish for free for reach, or set your price and sell on the SoClose store and marketplaces. You keep",
  "Publier gratuitement": "Publish for free",
  "Rechercher projets, fichiers, générateurs…": "Search projects, files, generators…",
  "Routé via CreditsForge / ComputeForge / Ollama, exécuté + validé par l'INSPECTOR.": "Routed via CreditsForge / ComputeForge / Ollama, executed + validated by the INSPECTOR.",
  "STL · 3MF · OBJ · ZIP — jusqu'à 500 Mo": "STL · 3MF · OBJ · ZIP — up to 500 MB",
  "Suivant": "Next",
  "Support de caméra inclinable 60°": "60° tilting camera mount",
  "Supprimé": "Deleted",
  "Tags": "Tags",
  "Tendances du moment": "Trending now",
  "Titre": "Title",
  "Ton nom": "Your name",
  "Tous les groupes": "All groups",
  "Tout voir": "See all",
  "Tu fixes le prix. Encaisse via la boutique SoClose ou un canal payant.": "You set the price. Get paid via the SoClose store or a paid channel.",
  "Tu touches": "You earn",
  "Usage, réglages conseillés, matière…": "Usage, recommended settings, material…",
  "Validé par l'INSPECTOR avant mise en ligne. Upload réel : credentials du site + confirmation.": "Validated by the INSPECTOR before going live. Real upload: site credentials + confirmation.",
  "Vide = sans groupe": "Empty = no group",
  "aucun e-mail défini": "no email set",
  "colle ta clé x-api-key…": "paste your x-api-key…",
  "configurées": "configured",
  "fichier(s)": "file(s)",
  "généré · IA": "generated · AI",
  "idée → maillage validé → G-code routé": "idea → validated mesh → routed G-code",
  "manuel": "manual",
  "ou par e-mail": "or by email",
  "partagé": "shared",
  "projet(s)": "project(s)",
  "prêt": "ready",
  "sur les ventes directes.": "on direct sales.",
  "sélection": "curated",
  "validés": "validated",
  "vends tes fichiers": "sell your files",
  "voie A · IA": "path A · AI",
  "voie B · paramétrique déterministe · sans IA": "path B · deterministic parametric · no AI",
  "à revoir": "to review",
  "— gratuit ou au prix que tu fixes.": "— free or at the price you set.",
  "Prêts à imprimer": "Ready to print",
  "Partagés": "Shared",
  "Vides": "Empty",
  "Conception & génération": "Design & generation",
  "Gate de printabilité": "Printability gate",
  "Production & routage": "Production & routing",
  "Texte, image ou ton propre STL — l'IA conçoit du CAD validé.": "Text, image or your own STL — the AI designs validated CAD.",
  "Manifold, parois, volume : seul l'imprimable passe.": "Manifold, walls, volume: only the printable passes.",
  "Télécharge, route vers ta ferme, ou mets en vente.": "Download, route to your farm, or put on sale.",
  "Produit": "Product",
  "Créateurs": "Creators",
  "Ressources": "Resources",
  "Marketplaces": "Marketplaces",
  "Revenus": "Earnings",
  "Documentation": "Documentation",
  "API": "API",
  "INSPECTOR": "INSPECTOR",
  "Statut": "Status",
  "Rejoins la communauté des makers": "Join the maker community",
  "Lien du groupe Facebook bientôt disponible": "Facebook group link coming soon",
  "Échange avec les autres créateurs, montre tes impressions, pose tes questions et découvre les dernières tendances 3D.": "Connect with other makers, show your prints, ask questions and discover the latest 3D trends.",
  "Confidentialité": "Privacy", "Conditions": "Terms", "Cookies": "Cookies",
  "Remboursement": "Refund", "Mentions légales": "Legal notice", "Prix en": "Prices in",
  "Compatible avec vos outils": "Compatible with your tools",
  "Tout ton écosystème, déjà connecté": "Your whole ecosystem, already connected",
  "Importe, valide et route vers les imprimantes, slicers, firmwares, CAO et marketplaces que tu utilises déjà.": "Import, validate and route to the printers, slicers, firmwares, CAD and marketplaces you already use.",
  "Explorer": "Explore", "Tendances": "Trending", "Récents": "Recent", "Tarifs": "Pricing",
  "Catégories": "Categories", "Vendre": "Sell", "Pipeline": "Pipeline", "Accès": "Access",
  "Téléverser & vendre": "Upload & sell", "Téléverser": "Upload", "Se connecter": "Sign in",
  "Créer un compte": "Sign up", "Ouvrir le Studio": "Open the Studio", "Dashboard": "Dashboard",
  "Crée des objets 3D imprimables, en une phrase.": "Create printable 3D objects, in one sentence.",
  "Atelier public · idée → imprimable": "Public workshop · idea → printable",
  "Décris l'objet à imprimer…": "Describe the object to print…",
  "Décris l'image source / glisse une photo…": "Describe the source image / drop a photo…",
  "Générer": "Generate", "Texte → 3D": "Text → 3D", "Image → 3D": "Image → 3D", "exemples": "examples",
  "À la une · généré par IA": "Featured · AI-generated", "Créer le mien": "Create mine",
  "modèles publiés": "models published", "créateurs actifs": "active creators",
  "impressions validées": "validated prints", "passées au gate INSPECTOR": "passed the INSPECTOR gate",
  "Comment ça marche": "How it works", "Décris ou téléverse": "Describe or upload",
  "L'INSPECTOR valide": "INSPECTOR validates", "Imprime ou vends": "Print or sell",
  "Catégorie": "Category", "Type": "Type", "Cas d'usage": "Use case", "Prix": "Price",
  "Trier": "Sort", "Tous": "All", "En vedette": "Featured", "Gratuit": "Free", "À vendre": "For sale",
  "CAD paramétrique": "Parametric CAD", "Génératif": "Generative", "Recommandés": "Recommended",
  "Populaires": "Popular", "modèle(s)": "model(s)", "STL": "STL", "Acheter": "Buy", "Remixer": "Remix",
  "Générer similaire": "Generate similar", "Maison": "Home", "Atelier": "Workshop", "Déco": "Decor",
  "RC & Drone": "RC & Drone", "Mécanique": "Mechanical", "Optique": "Optics",
  "Pour les créateurs": "For creators", "Transforme tes modèles en revenus.": "Turn your models into revenue.",
  "Téléverser un modèle": "Upload a model", "Devenir créateur": "Become a creator",
  "reversés au créateur": "paid to the creator", "pour publier": "to publish", "marketplaces": "marketplaces",
  "délai de paiement": "payout time", "Choisis ta formule": "Choose your plan",
  "Commence gratuitement, passe au niveau supérieur quand tu vends.": "Start free, upgrade when you sell.",
  "Le plus populaire": "Most popular", "Par mois": "Per month", "Gratuit pour toujours": "Free forever",
  "Sur devis": "Custom", "Parcourir par catégorie": "Browse by category",
  "Trouve la pièce qu'il te faut, par usage.": "Find the part you need, by use case.",
  "Les modèles qui montent": "What's trending", "Les plus téléchargés et likés cette semaine.": "The most downloaded and liked this week.",
  "Les dernières créations": "The latest creations", "Fraîchement généré et validé par l'INSPECTOR.": "Freshly generated and INSPECTOR-validated.",
  "Voir la catégorie": "View category", "pièces": "pieces", "Langue": "Language",
  // -- Admin shell --
  "Créer": "Create", "Projets": "Projects", "Studio 3D": "3D Studio", "Générateurs": "Generators",
  "Production": "Production", "Imprimantes": "Printers", "Filaments": "Filaments", "Intégrations": "Integrations",
  "Communauté": "Community", "Profil": "Profile", "Réglages": "Settings",
  "Création": "Creation", "Système": "System", "Compte": "Account", "idée → imprimable": "idea → printable",
  // -- Common actions --
  "Enregistrer": "Save", "Annuler": "Cancel", "Supprimer": "Delete", "Dupliquer": "Duplicate", "Partager": "Share",
  "Modifier": "Edit", "Configurer": "Configure", "Rafraîchir": "Refresh", "Voir mes projets": "View my projects",
  "Définir une clé API": "Set an API key", "Clé API définie": "API key set",
  // -- Page subtitles --
  "Vue d'ensemble de ton atelier de production 3D.": "Overview of your 3D production workshop.",
  "Tes projets — crée, ouvre, duplique, partage.": "Your projects — create, open, duplicate, share.",
  "Décris ton idée, l'IA conçoit du CAD paramétrique validé — ou pars d'un générateur.": "Describe your idea, the AI designs validated parametric CAD — or start from a generator.",
  "De l'idée au fichier validé — décris en langage naturel, ou pars d'un template paramétrique.": "From idea to validated file — describe in natural language, or start from a parametric template.",
  "Suivi des impressions en direct et historique.": "Live print monitoring and history.",
  "Ta ferme d'impression — Moonraker / OctoPrint / PrusaLink.": "Your print farm — Moonraker / OctoPrint / PrusaLink.",
  "Inventaire des bobines de filament.": "Filament spool inventory.",
  "Connecte tes services — LLM, image 3D, slicer, imprimantes, stockage.": "Connect your services — LLM, 3D image, slicer, printers, storage.",
  "La bibliothèque de générateurs paramétriques.": "The parametric generator library.",
  // -- Settings / Profile / Community --
  "Apparence": "Appearance", "Accent": "Accent", "La couleur primaire de l'interface.": "The primary UI color.",
  "Densité": "Density", "L'espacement des éléments.": "Element spacing.", "Langue du site vitrine.": "Vitrine language.",
  "Compact": "Compact", "Normal": "Normal", "Aéré": "Comfortable", "Accès API": "API access", "Clé API": "API key",
  "Nom": "Name", "E-mail": "Email", "Se déconnecter": "Sign out", "Effacer": "Clear",
  "Apparence, accès et compte — synchronisés avec la barre latérale.": "Appearance, access and account — synced with the sidebar.",
  "Ton compte, ton usage et l'état de tes intégrations.": "Your account, usage and integration status.",
  "Projets actifs": "Active projects", "Fichiers 3D": "3D files", "Impressions": "Prints", "Crédits": "Credits",
  "Accès API actif": "API access active", "Accès en lecture seule": "Read-only access",
  "Rejoins les makers 3DPrintForge — entraide, idées et partages.": "Join the 3DPrintForge makers — help, ideas and sharing.",
  "Rejoins notre groupe Facebook": "Join our Facebook group", "Rejoindre le groupe": "Join the group", "Bientôt disponible": "Coming soon",
  // -- Page titles + subs --
  "Générateurs paramétriques": "Parametric generators", "Imprimantes & matériaux": "Printers & materials",
  "Intégrations & configuration": "Integrations & configuration",
  "Créez, organisez en groupes, prévisualisez et partagez vos modèles.": "Create, group, preview and share your models.",
  "Pièces fonctionnelles, dimensionnées pour ta machine, validées par l'INSPECTOR.": "Functional parts, sized for your machine, validated by the INSPECTOR.",
  "De l'idée au fichier 3D prêt à imprimer.": "From idea to print-ready 3D file.",
  "Suivi live et historique des impressions routées vers la ferme (Moonraker / OctoPrint / PrusaLink).": "Live monitoring and history of prints routed to the farm (Moonraker / OctoPrint / PrusaLink).",
  "Inventaire des bobines : poids restant, coût, alertes stock bas. Déduit à chaque impression.": "Spool inventory: remaining weight, cost, low-stock alerts. Deducted on every print.",
  "Tous les outils du pipeline, leur statut et leurs templates de config — prêts à brancher.": "Every pipeline tool, its status and config templates — ready to plug in.",
  "La ferme (réseau, statut live), les profils machine et les matières.": "The farm (network, live status), machine profiles and materials.",
  // -- Motivational hero (Dashboard / Create) --
  "Bon retour": "Welcome back", "Prêt à créer ?": "Ready to create?",
  "Transforme une idée en pièce imprimable, validée pour ta machine — en une phrase.": "Turn an idea into a printable part, validated for your machine — in one sentence.",
  "Donne vie à ton idée": "Bring your idea to life",
  "Décris, génère, valide, imprime. Ton prochain modèle commence ici.": "Describe, generate, validate, print. Your next model starts here.",
  "Nouveau modèle": "New model",
};
const ES: Record<string, string> = {
  "90 % reversés sur tes ventes": "90% para ti en tus ventas",
  "Ajoute-en une dans la Ferme": "Añade una en la Granja",
  "Ajuster le restant": "Ajustar restante",
  "Aperçu du projet": "Vista general del proyecto",
  "Aucun fichier": "Sin archivos",
  "Aucun fichier. Génère un objet ou décris une idée ci-dessus.": "Sin archivos. Genera un objeto o describe una idea arriba.",
  "Aucune bobine. Ajoute ton inventaire de filament pour suivre conso et coûts.": "Sin bobinas. Añade tu inventario de filamento para seguir consumo y costes.",
  "Aucune impression en cours. Routez un fichier prêt à imprimer depuis un projet.": "Sin impresiones en curso. Enruta un archivo listo para imprimir desde un proyecto.",
  "Aucune imprimante enregistrée.": "Ninguna impresora registrada.",
  "Boutique store.soclose.co dédiée": "Tienda store.soclose.co dedicada",
  "CAD paramétrique (LLM)": "CAD paramétrico (LLM)",
  "CAD paramétrique + 19 générateurs": "CAD paramétrico + 19 generadores",
  "Ce projet partagé ne contient aucun fichier.": "Este proyecto compartido no contiene archivos.",
  "Configuré": "Configurado",
  "Connecteurs MMF / Thingiverse": "Conectores MMF / Thingiverse",
  "Cookies & confidentialité": "Cookies y privacidad",
  "Critique de conception": "Crítica de diseño",
  "Depuis une idée · IA": "Desde una idea · IA",
  "Dernière mise à jour :": "Última actualización:",
  "Détails": "Detalles",
  "Détails du job": "Detalles del trabajo",
  "En file": "En cola",
  "Envoyé": "Enviado",
  "Erreur": "Error",
  "Essentiels": "Esenciales",
  "Export STL / 3MF / G-code": "Exportación STL / 3MF / G-code",
  "G-code requis : tranche le modèle d'abord.": "G-code requerido: primero lamina el modelo.",
  "Gate INSPECTOR illimité": "Gate INSPECTOR ilimitado",
  "Générateur": "Generador",
  "Générateur paramétrique": "Generador paramétrico",
  "Génération IA (Ollama local)": "Generación IA (Ollama local)",
  "Génération IA cloud (TRELLIS / Hunyuan3D)": "Generación IA en la nube (TRELLIS / Hunyuan3D)",
  "Impression": "Imprimiendo",
  "Imprimante · matière": "Impresora · material",
  "Lancer sur la ferme": "Lanzar en la granja",
  "Lien de partage invalide ou expiré.": "Enlace compartido no válido o caducado.",
  "Local · prêt": "Local · listo",
  "Machine · matière": "Máquina · material",
  "Maillage génératif (fal/kai)": "Malla generativa (fal/kai)",
  "Matières": "Materiales",
  "Matériaux": "Materiales",
  "Mesure d'audience anonymisée pour améliorer le service.": "Analítica anonimizada para mejorar el servicio.",
  "Multi-imprimantes + Spoolman": "Multi-impresora + Spoolman",
  "Nom de l'imprimante": "Nombre de la impresora",
  "Note · dev · amélioration": "Nota · dev · mejora",
  "Nous utilisons des cookies pour sécuriser le site, comprendre son usage et améliorer nos services. Les cookies essentiels sont toujours actifs. Vos données sont traitées conformément à la": "Usamos cookies para proteger el sitio, entender su uso y mejorar nuestros servicios. Las cookies esenciales están siempre activas. Tus datos se tratan conforme a la",
  "Nécessaires au fonctionnement (session, sécurité).": "Necesarias para el funcionamiento (sesión, seguridad).",
  "Parcours libre de la galerie": "Exploración libre de la galería",
  "Pas encore d'impressions terminées.": "Aún no hay impresiones terminadas.",
  "Personnalisation et mesure des campagnes.": "Personalización y medición de campañas.",
  "Personnaliser": "Personalizar",
  "Pièce précédente": "Pieza anterior",
  "Pièce suivante": "Pieza siguiente",
  "Pièces": "Piezas",
  "Planifié": "Programado",
  "Politique cookies": "Política de cookies",
  "Politique de confidentialité": "Política de privacidad",
  "Pour toute question de protection des données ou question légale, contactez": "Para cualquier cuestión de protección de datos o legal, contacta con",
  "Projet partagé · lecture seule": "Proyecto compartido · solo lectura",
  "Préférences cookies": "Preferencias de cookies",
  "Prêtes": "Listas",
  "Quantité (copies)": "Cantidad (copias)",
  "Rapport de printabilité": "Informe de imprimibilidad",
  "Rechercher projets, fichiers, générateurs, pages…": "Buscar proyectos, archivos, generadores, páginas…",
  "Refuser le non-essentiel": "Rechazar lo no esencial",
  "Retour": "Volver",
  "Retour à l'accueil": "Volver al inicio",
  "Routage ferme d'impression": "Enrutado a granja de impresión",
  "SLA & facturation entreprise": "SLA y facturación empresarial",
  "Sans groupe": "Sin grupo",
  "Singapour": "Singapur",
  "Slice / estimer (temps · filament · coût)": "Laminar / estimar (tiempo · filamento · coste)",
  "Slicing PrusaSlicer + devis": "Laminado PrusaSlicer + presupuestos",
  "Support prioritaire": "Soporte prioritario",
  "Template de configuration": "Plantilla de configuración",
  "Terminé": "Terminado",
  "Tout Maker, plus :": "Todo Maker, más:",
  "Tout Studio, plus :": "Todo Studio, más:",
  "Tout accepter": "Aceptar todo",
  "Vente sur 12 marketplaces": "Venta en 12 marketplaces",
  "au titre du": "en virtud de la",
  "batch · planifié": "lote · programado",
  "de": "de",
  "prêts à imprimer": "listos para imprimir",
  "rendu global · toutes les pièces": "render global · todas las piezas",
  "timing · notes · améliorations": "tiempos · notas · mejoras",
  "À configurer": "Por configurar",
  "À lancer": "Listo para lanzar",
  "Éditer": "Editar",
  "Étapes": "Etapas",
  "État": "Estado",
  "éclatement": "vista explosionada",
  "/ vente sur la boutique SoClose.": "/ venta en la tienda SoClose.",
  "1 clic → prompt": "1 clic → prompt",
  "API hors-ligne": "API sin conexión",
  "Apparence, clé API, langue, compte.": "Apariencia, clave API, idioma, cuenta.",
  "Aucun job. Routez un modèle validé vers une imprimante.": "Sin trabajos. Enruta un modelo validado a una impresora.",
  "Aucun projet ne correspond. Crée un projet ou ajuste les filtres.": "Ningún proyecto coincide. Crea un proyecto o ajusta los filtros.",
  "Aucun projet.": "Aún no hay proyectos.",
  "Bibliothèque de templates": "Biblioteca de plantillas",
  "Bras articulé, boîtier capteur…": "Brazo articulado, carcasa de sensor…",
  "Clé effacée": "Clave borrada",
  "Clé requise pour voir l'état": "Se requiere clave para ver el estado",
  "Commerciale (vente)": "Comercial (venta)",
  "Conception…": "Diseñando…",
  "Confort": "Amplio",
  "Connexion": "Iniciar sesión",
  "Continuer avec GitHub": "Continuar con GitHub",
  "Continuer avec Google": "Continuar con Google",
  "Créateur": "Creador",
  "Créer le projet": "Crear el proyecto",
  "Créer le projet + concevoir": "Crear el proyecto + diseñar",
  "Créer mon compte": "Crear mi cuenta",
  "Créez-en un": "Crea uno",
  "Dernières générations": "Últimas generaciones",
  "Description": "Descripción",
  "Destinations": "Destinos",
  "Dupliqué": "Duplicado",
  "Déconnecté": "Sesión cerrada",
  "Décris ton idée": "Describe tu idea",
  "En créant un compte, tu acceptes les conditions et la politique de confidentialité.": "Al crear una cuenta, aceptas los términos y la política de privacidad.",
  "En vente": "En venta",
  "Fait à Singapour": "Hecho en Singapur",
  "Filtrer les projets…": "Filtrar proyectos…",
  "Glisse ton fichier ou clique pour choisir": "Arrastra tu archivo o haz clic para elegir",
  "Groupe / collection": "Grupo / colección",
  "Génération…": "Generando…",
  "Générer + valider": "Generar + validar",
  "Géométrie exacte via le CAD kernel, puis gate INSPECTOR avant ajout au projet.": "Geometría exacta vía el kernel CAD, luego el gate INSPECTOR antes de añadir al proyecto.",
  "Impression en cours": "Imprimiendo ahora",
  "Inscription": "Registrarse",
  "Inscris-toi": "Regístrate",
  "L'atelier 3D de bout en bout : idée → maillage validé → imprimé ou vendu.": "El taller 3D de principio a fin: idea → malla validada → impreso o vendido.",
  "Les lectures publiques (galerie, previews) ne nécessitent rien. Les actions (créer, router, publier) et les données opérationnelles exigent ta clé": "Las lecturas públicas (galería, vistas previas) no requieren nada. Las acciones (crear, enrutar, publicar) y los datos operativos exigen tu clave",
  "Licence": "Licencia",
  "Lien de partage copié": "Enlace para compartir copiado",
  "Légal": "Legal",
  "Machine": "Máquina",
  "Matière": "Material",
  "Mettre en vente": "Poner en venta",
  "Modifier (nom, groupe)": "Editar (nombre, grupo)",
  "Modifier le projet": "Editar el proyecto",
  "Mon support de caméra": "Mi soporte de cámara",
  "Monétisation": "Monetización",
  "Mot de passe": "Contraseña",
  "Nom du projet (optionnel)": "Nombre del proyecto (opcional)",
  "Nouveau projet": "Nuevo proyecto",
  "Optionnel — contexte, contraintes, machine cible…": "Opcional — contexto, restricciones, máquina objetivo…",
  "Ouvrir": "Abrir",
  "Ouvrir dans le Studio 3D": "Abrir en el Studio 3D",
  "Paramètres": "Parámetros",
  "Parcours librement la galerie. Pour télécharger, remixer ou générer, crée ton compte. Téléverse et": "Explora la galería libremente. Para descargar, remezclar o generar, crea tu cuenta. Sube y",
  "Partage libre pour un max de portée et de téléchargements.": "Compartir gratis para el máximo alcance y descargas.",
  "Pas encore de compte ?": "¿Aún no tienes cuenta?",
  "Prix (€)": "Precio (€)",
  "Profil enregistré": "Perfil guardado",
  "Projet créé": "Proyecto creado",
  "Projet mis à jour": "Proyecto actualizado",
  "Projets récents": "Proyectos recientes",
  "Précédent": "Anterior",
  "Publie gratuitement pour la portée, ou fixe ton prix et vends sur la boutique SoClose et les marketplaces. Tu gardes": "Publica gratis para el alcance, o fija tu precio y vende en la tienda SoClose y los marketplaces. Te quedas con",
  "Publier gratuitement": "Publicar gratis",
  "Rechercher projets, fichiers, générateurs…": "Buscar proyectos, archivos, generadores…",
  "Routé via CreditsForge / ComputeForge / Ollama, exécuté + validé par l'INSPECTOR.": "Enrutado vía CreditsForge / ComputeForge / Ollama, ejecutado + validado por el INSPECTOR.",
  "STL · 3MF · OBJ · ZIP — jusqu'à 500 Mo": "STL · 3MF · OBJ · ZIP — hasta 500 MB",
  "Suivant": "Siguiente",
  "Support de caméra inclinable 60°": "Soporte de cámara inclinable 60°",
  "Supprimé": "Eliminado",
  "Tags": "Etiquetas",
  "Tendances du moment": "Tendencias del momento",
  "Titre": "Título",
  "Ton nom": "Tu nombre",
  "Tous les groupes": "Todos los grupos",
  "Tout voir": "Ver todo",
  "Tu fixes le prix. Encaisse via la boutique SoClose ou un canal payant.": "Tú fijas el precio. Cobra vía la tienda SoClose o un canal de pago.",
  "Tu touches": "Ganas",
  "Usage, réglages conseillés, matière…": "Uso, ajustes recomendados, material…",
  "Validé par l'INSPECTOR avant mise en ligne. Upload réel : credentials du site + confirmation.": "Validado por el INSPECTOR antes de publicarse. Subida real: credenciales del sitio + confirmación.",
  "Vide = sans groupe": "Vacío = sin grupo",
  "aucun e-mail défini": "sin correo definido",
  "colle ta clé x-api-key…": "pega tu clave x-api-key…",
  "configurées": "configuradas",
  "fichier(s)": "archivo(s)",
  "généré · IA": "generado · IA",
  "idée → maillage validé → G-code routé": "idea → malla validada → G-code enrutado",
  "manuel": "manual",
  "ou par e-mail": "o por correo",
  "partagé": "compartido",
  "projet(s)": "proyecto(s)",
  "prêt": "listo",
  "sur les ventes directes.": "en ventas directas.",
  "sélection": "selección",
  "validés": "validados",
  "vends tes fichiers": "vende tus archivos",
  "voie A · IA": "vía A · IA",
  "voie B · paramétrique déterministe · sans IA": "vía B · paramétrico determinista · sin IA",
  "à revoir": "a revisar",
  "— gratuit ou au prix que tu fixes.": "— gratis o al precio que fijes.",
  "Prêts à imprimer": "Listos para imprimir",
  "Partagés": "Compartidos",
  "Vides": "Vacíos",
  "Conception & génération": "Diseño y generación",
  "Gate de printabilité": "Gate de imprimibilidad",
  "Production & routage": "Producción y enrutado",
  "Texte, image ou ton propre STL — l'IA conçoit du CAD validé.": "Texto, imagen o tu propio STL — la IA diseña CAD validado.",
  "Manifold, parois, volume : seul l'imprimable passe.": "Manifold, paredes, volumen: solo pasa lo imprimible.",
  "Télécharge, route vers ta ferme, ou mets en vente.": "Descarga, enruta a tu granja o pon en venta.",
  "Produit": "Producto",
  "Créateurs": "Creadores",
  "Ressources": "Recursos",
  "Marketplaces": "Marketplaces",
  "Revenus": "Ingresos",
  "Documentation": "Documentación",
  "API": "API",
  "INSPECTOR": "INSPECTOR",
  "Statut": "Estado",
  "Rejoins la communauté des makers": "Únete a la comunidad de makers",
  "Lien du groupe Facebook bientôt disponible": "Enlace del grupo de Facebook próximamente",
  "Échange avec les autres créateurs, montre tes impressions, pose tes questions et découvre les dernières tendances 3D.": "Conecta con otros creadores, muestra tus impresiones, haz preguntas y descubre las últimas tendencias 3D.",
  "Confidentialité": "Privacidad", "Conditions": "Términos", "Cookies": "Cookies",
  "Remboursement": "Reembolso", "Mentions légales": "Aviso legal", "Prix en": "Precios en",
  "Compatible avec vos outils": "Compatible con tus herramientas",
  "Tout ton écosystème, déjà connecté": "Todo tu ecosistema, ya conectado",
  "Importe, valide et route vers les imprimantes, slicers, firmwares, CAO et marketplaces que tu utilises déjà.": "Importa, valida y enruta a las impresoras, slicers, firmwares, CAD y marketplaces que ya usas.",
  "Explorer": "Explorar", "Tendances": "Tendencias", "Récents": "Recientes", "Tarifs": "Precios",
  "Catégories": "Categorías", "Vendre": "Vender", "Pipeline": "Pipeline", "Accès": "Acceso",
  "Téléverser & vendre": "Subir y vender", "Téléverser": "Subir", "Se connecter": "Entrar",
  "Créer un compte": "Crear cuenta", "Ouvrir le Studio": "Abrir el Studio", "Dashboard": "Panel",
  "Crée des objets 3D imprimables, en une phrase.": "Crea objetos 3D imprimibles, en una frase.",
  "Atelier public · idée → imprimable": "Taller público · idea → imprimible",
  "Décris l'objet à imprimer…": "Describe el objeto a imprimir…",
  "Décris l'image source / glisse une photo…": "Describe la imagen / suelta una foto…",
  "Générer": "Generar", "Texte → 3D": "Texto → 3D", "Image → 3D": "Imagen → 3D", "exemples": "ejemplos",
  "À la une · généré par IA": "Destacado · generado por IA", "Créer le mien": "Crear el mío",
  "modèles publiés": "modelos publicados", "créateurs actifs": "creadores activos",
  "impressions validées": "impresiones validadas", "passées au gate INSPECTOR": "pasaron el gate INSPECTOR",
  "Comment ça marche": "Cómo funciona", "Décris ou téléverse": "Describe o sube",
  "L'INSPECTOR valide": "INSPECTOR valida", "Imprime ou vends": "Imprime o vende",
  "Catégorie": "Categoría", "Type": "Tipo", "Cas d'usage": "Uso", "Prix": "Precio",
  "Trier": "Ordenar", "Tous": "Todos", "En vedette": "Destacados", "Gratuit": "Gratis", "À vendre": "En venta",
  "CAD paramétrique": "CAD paramétrico", "Génératif": "Generativo", "Recommandés": "Recomendados",
  "Populaires": "Populares", "modèle(s)": "modelo(s)", "STL": "STL", "Acheter": "Comprar", "Remixer": "Remezclar",
  "Générer similaire": "Generar similar", "Maison": "Hogar", "Atelier": "Taller", "Déco": "Decoración",
  "RC & Drone": "RC y Dron", "Mécanique": "Mecánica", "Optique": "Óptica",
  "Pour les créateurs": "Para creadores", "Transforme tes modèles en revenus.": "Convierte tus modelos en ingresos.",
  "Téléverser un modèle": "Subir un modelo", "Devenir créateur": "Hazte creador",
  "reversés au créateur": "para el creador", "pour publier": "para publicar", "marketplaces": "marketplaces",
  "délai de paiement": "plazo de pago", "Choisis ta formule": "Elige tu plan",
  "Commence gratuitement, passe au niveau supérieur quand tu vends.": "Empieza gratis, mejora cuando vendas.",
  "Le plus populaire": "El más popular", "Par mois": "Al mes", "Gratuit pour toujours": "Gratis para siempre",
  "Sur devis": "A medida", "Parcourir par catégorie": "Explorar por categoría",
  "Trouve la pièce qu'il te faut, par usage.": "Encuentra la pieza que necesitas, por uso.",
  "Les modèles qui montent": "Lo que es tendencia", "Les plus téléchargés et likés cette semaine.": "Lo más descargado y gustado esta semana.",
  "Les dernières créations": "Las últimas creaciones", "Fraîchement généré et validé par l'INSPECTOR.": "Recién generado y validado por INSPECTOR.",
  "Voir la catégorie": "Ver categoría", "pièces": "piezas", "Langue": "Idioma",
  // -- Admin shell --
  "Créer": "Crear", "Projets": "Proyectos", "Studio 3D": "Studio 3D", "Générateurs": "Generadores",
  "Production": "Producción", "Imprimantes": "Impresoras", "Filaments": "Filamentos", "Intégrations": "Integraciones",
  "Communauté": "Comunidad", "Profil": "Perfil", "Réglages": "Ajustes",
  "Création": "Creación", "Système": "Sistema", "Compte": "Cuenta", "idée → imprimable": "idea → imprimible",
  // -- Common actions --
  "Enregistrer": "Guardar", "Annuler": "Cancelar", "Supprimer": "Eliminar", "Dupliquer": "Duplicar", "Partager": "Compartir",
  "Modifier": "Editar", "Configurer": "Configurar", "Rafraîchir": "Actualizar", "Voir mes projets": "Ver mis proyectos",
  "Définir une clé API": "Definir una clave API", "Clé API définie": "Clave API definida",
  // -- Page subtitles --
  "Vue d'ensemble de ton atelier de production 3D.": "Vista general de tu taller de producción 3D.",
  "Tes projets — crée, ouvre, duplique, partage.": "Tus proyectos — crea, abre, duplica, comparte.",
  "Décris ton idée, l'IA conçoit du CAD paramétrique validé — ou pars d'un générateur.": "Describe tu idea, la IA diseña CAD paramétrico validado — o parte de un generador.",
  "De l'idée au fichier validé — décris en langage naturel, ou pars d'un template paramétrique.": "De la idea al archivo validado — describe en lenguaje natural, o parte de una plantilla paramétrica.",
  "Suivi des impressions en direct et historique.": "Seguimiento de impresiones en vivo e historial.",
  "Ta ferme d'impression — Moonraker / OctoPrint / PrusaLink.": "Tu granja de impresión — Moonraker / OctoPrint / PrusaLink.",
  "Inventaire des bobines de filament.": "Inventario de bobinas de filamento.",
  "Connecte tes services — LLM, image 3D, slicer, imprimantes, stockage.": "Conecta tus servicios — LLM, imagen 3D, slicer, impresoras, almacenamiento.",
  "La bibliothèque de générateurs paramétriques.": "La biblioteca de generadores paramétricos.",
  // -- Settings / Profile / Community --
  "Apparence": "Apariencia", "Accent": "Acento", "La couleur primaire de l'interface.": "El color primario de la interfaz.",
  "Densité": "Densidad", "L'espacement des éléments.": "El espaciado de los elementos.", "Langue du site vitrine.": "Idioma del sitio público.",
  "Compact": "Compacto", "Normal": "Normal", "Aéré": "Amplio", "Accès API": "Acceso API", "Clé API": "Clave API",
  "Nom": "Nombre", "E-mail": "Correo", "Se déconnecter": "Cerrar sesión", "Effacer": "Borrar",
  "Apparence, accès et compte — synchronisés avec la barre latérale.": "Apariencia, acceso y cuenta — sincronizados con la barra lateral.",
  "Ton compte, ton usage et l'état de tes intégrations.": "Tu cuenta, tu uso y el estado de tus integraciones.",
  "Projets actifs": "Proyectos activos", "Fichiers 3D": "Archivos 3D", "Impressions": "Impresiones", "Crédits": "Créditos",
  "Accès API actif": "Acceso API activo", "Accès en lecture seule": "Acceso de solo lectura",
  "Rejoins les makers 3DPrintForge — entraide, idées et partages.": "Únete a los makers 3DPrintForge — ayuda, ideas y compartir.",
  "Rejoins notre groupe Facebook": "Únete a nuestro grupo de Facebook", "Rejoindre le groupe": "Unirse al grupo", "Bientôt disponible": "Próximamente",
  // -- Page titles + subs --
  "Générateurs paramétriques": "Generadores paramétricos", "Imprimantes & matériaux": "Impresoras y materiales",
  "Intégrations & configuration": "Integraciones y configuración",
  "Créez, organisez en groupes, prévisualisez et partagez vos modèles.": "Crea, agrupa, previsualiza y comparte tus modelos.",
  "Pièces fonctionnelles, dimensionnées pour ta machine, validées par l'INSPECTOR.": "Piezas funcionales, dimensionadas para tu máquina, validadas por INSPECTOR.",
  "De l'idée au fichier 3D prêt à imprimer.": "De la idea al archivo 3D listo para imprimir.",
  "Suivi live et historique des impressions routées vers la ferme (Moonraker / OctoPrint / PrusaLink).": "Seguimiento en vivo e historial de impresiones enviadas a la granja (Moonraker / OctoPrint / PrusaLink).",
  "Inventaire des bobines : poids restant, coût, alertes stock bas. Déduit à chaque impression.": "Inventario de bobinas: peso restante, coste, alertas de stock bajo. Se descuenta en cada impresión.",
  "Tous les outils du pipeline, leur statut et leurs templates de config — prêts à brancher.": "Todas las herramientas del pipeline, su estado y plantillas de configuración — listas para conectar.",
  "La ferme (réseau, statut live), les profils machine et les matières.": "La granja (red, estado en vivo), los perfiles de máquina y los materiales.",
  // -- Motivational hero --
  "Bon retour": "Bienvenido de nuevo", "Prêt à créer ?": "¿Listo para crear?",
  "Transforme une idée en pièce imprimable, validée pour ta machine — en une phrase.": "Convierte una idea en una pieza imprimible, validada para tu máquina — en una frase.",
  "Donne vie à ton idée": "Da vida a tu idea",
  "Décris, génère, valide, imprime. Ton prochain modèle commence ici.": "Describe, genera, valida, imprime. Tu próximo modelo empieza aquí.",
  "Nouveau modèle": "Nuevo modelo",
};
const DICT: Record<Lang, Record<string, string>> = { fr: {}, en: EN, es: ES };

const LangCtx = createContext<{ lang: Lang; setLang: (l: Lang) => void }>({ lang: "fr", setLang: () => {} });

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => (localStorage.getItem("pf_lang") as Lang) || "fr");
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  const setLang = (l: Lang) => { localStorage.setItem("pf_lang", l); setLangState(l); };
  return <LangCtx.Provider value={{ lang, setLang }}>{children}</LangCtx.Provider>;
}

export function useLang() { return useContext(LangCtx); }
export function useT() {
  const { lang } = useContext(LangCtx);
  return (fr: string): string => DICT[lang][fr] ?? fr;
}
